import { updateGameStateAfterSubmission } from "@/utils/gameStateUpdates";
import { debugLog, DebugLevel } from "@/utils/debugUtils";
import { saveUserWordToDatabase, fetchBotWordFromDatabase } from "./databaseOperations";
import { handleBotStillThinking } from "./stateManagement";

/**
 * Processes the word submission including database operations
 */
export const processWordSubmission = async (
  gameId: string,
  roundNumber: number,
  currentWord: string,
  usedWords: Set<string>,
  setRounds: (rounds: any) => void,
  setUsedWords: (usedWords: any) => void,
  setCurrentWord: (word: string) => void,
  setIsRevealed: (isRevealed: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setGameStatus: (status: string) => void,
  setUserHasSubmitted: (hasSubmitted: boolean) => void,
  setProcessingComplete: (complete: boolean) => void,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setWaitingForBotWord: (waiting: boolean) => void,
  toast: any,
  handleProcessBothWords: (aiWord: string, aiExplanation: string | undefined) => Promise<void>
) => {
  try {
    // Save the user's word to the database
    const saveResult = await saveUserWordToDatabase(gameId, roundNumber, currentWord);
    if (!saveResult.success) {
      throw new Error("Failed to save user word");
    }
    
    // Mark the user as having submitted their word
    setUserHasSubmitted(true);
    debugLog(DebugLevel.INFO, "wordSubmission", "User has submitted their word, userHasSubmitted set to true");
    
    // Check if bot has already chosen its word
    const botWordResult = await fetchBotWordFromDatabase(gameId, roundNumber);
    
    debugLog(DebugLevel.INFO, "wordSubmission", `Bot has word in DB: ${botWordResult.hasBotWord}`);
    
    if (botWordResult.hasBotWord) {
      // Bot has chosen a word, process both words
      debugLog(DebugLevel.INFO, "wordSubmission", "Bot has chosen word. Processing both words now.");
      
      // Process both words since they're both available
      await updateGameStateAfterSubmission(
        gameId,
        roundNumber,
        currentWord.trim(),
        botWordResult.aiWord,
        botWordResult.aiExplanation,
        usedWords,
        setRounds,
        setUsedWords,
        setCurrentWord,
        setIsRevealed,
        setBotHasChosenWord,
        setBotIsThinking,
        setShouldAutoFocus,
        setGameOver,
        setConvergenceRound,
        setGameStatus,
        setUserHasSubmitted,
        setProcessingComplete,
        toast,
        setIsSubmitting
      );
    } else {
      // Bot hasn't chosen yet, wait for it
      handleBotStillThinking(
        setWaitingForBotWord,
        setBotIsThinking,
        setProcessingComplete,
        setIsSubmitting
      );
    }
    
    return { success: true };
  } catch (error) {
    debugLog(DebugLevel.ERROR, "wordSubmission", "Error in processWordSubmission:", error);
    return { success: false, error };
  }
};

/**
 * Handles the retry logic when bot word isn't initially available
 */
export const retryFetchingBotWord = async (
  gameId: string,
  roundNumber: number,
  currentWord: string,
  usedWords: Set<string>,
  setRounds: (rounds: any) => void,
  setUsedWords: (usedWords: any) => void,
  setCurrentWord: (word: string) => void,
  setIsRevealed: (isRevealed: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setGameStatus: (status: string) => void,
  setUserHasSubmitted: (hasSubmitted: boolean) => void,
  setProcessingComplete: (complete: boolean) => void,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setWaitingForBotWord: (waiting: boolean) => void,
  toast: any
) => {
  try {
    const botWordResult = await fetchBotWordFromDatabase(gameId, roundNumber);
    
    if (!botWordResult.success) throw botWordResult.error;
    
    if (botWordResult.hasBotWord) {
      debugLog(DebugLevel.INFO, "wordSubmission", "Retry successful, found bot's word:", botWordResult.aiWord);
      
      // Process both words now that we have them
      await updateGameStateAfterSubmission(
        gameId,
        roundNumber,
        currentWord.trim(),
        botWordResult.aiWord,
        botWordResult.aiExplanation,
        usedWords,
        setRounds,
        setUsedWords,
        setCurrentWord,
        setIsRevealed,
        setBotHasChosenWord,
        setBotIsThinking,
        setShouldAutoFocus,
        setGameOver,
        setConvergenceRound,
        setGameStatus,
        setUserHasSubmitted,
        setProcessingComplete,
        toast,
        setIsSubmitting
      );
      
      return { success: true };
    } else {
      debugLog(DebugLevel.ERROR, "wordSubmission", "Bot word still missing from database despite botHasChosenWord being true");
      
      // CRITICAL FIX: Instead of showing an error, set the waiting flag
      debugLog(DebugLevel.INFO, "wordSubmission", "Setting waitingForBotWord to true");
      setWaitingForBotWord(true);
      
      // Keep botIsThinking true to maintain checkmark visibility
      setBotIsThinking(true);
      setProcessingComplete(true);
      setIsSubmitting(false);
      
      return { success: false, waitingForBotResponse: true };
    }
  } catch (error) {
    debugLog(DebugLevel.ERROR, "wordSubmission", "Error in retry check:", error);
    setProcessingComplete(true);
    setIsSubmitting(false);
    
    // Set waiting flag in case of error too
    setWaitingForBotWord(true);
    
    return { success: false, error };
  }
};
